/**
 * PDF Viewer Component
 * Renders PDF with react-pdf using a presigned URL
 */

import React, { useState } from 'react'
import { Box, Button, Typography, CircularProgress, Alert } from '@mui/material'
import { Download } from '@mui/icons-material'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { ShowNotesPDFBlock } from '../types'

// Configure PDF.js worker using CDN - more reliable than bundler resolution
// Must be in same module as Document/Page components per react-pdf v10 requirements
// eslint-disable-next-line fp/no-mutation
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PDFViewerProps {
  block: ShowNotesPDFBlock
  presignedUrl: string | null | undefined
  loading?: boolean
  error?: Error | null
  width?: number
}

export function PDFViewer({
  block,
  presignedUrl,
  loading = false,
  error = null,
  width = 800,
}: PDFViewerProps) {
  let [numPages, setNumPages] = useState<number>(0)
  let [pageNumber, setPageNumber] = useState<number>(1)
  let [pdfError, setPdfError] = useState<string>('')
  let [isPageRendering, setIsPageRendering] = useState<boolean>(false)
  let [containerHeight, setContainerHeight] = useState<number | null>(null)
  let containerRef = React.useRef<HTMLDivElement>(null)

  let onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages)
    setPdfError('')
  }

  let onDocumentLoadError = () => {
    setPdfError('Failed to load PDF')
  }

  let onPageRenderSuccess = () => {
    setIsPageRendering(false)
    // Capture the height after first render to prevent layout shifts
    if (containerRef.current && containerHeight === null) {
      setContainerHeight(containerRef.current.offsetHeight)
    }
  }

  let handlePageChange = (newPage: number) => {
    setIsPageRendering(true)
    setPageNumber(newPage)
  }

  if (error) {
    return <Alert severity="error">Failed to load PDF: {error.message}</Alert>
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading PDF...
        </Typography>
      </Box>
    )
  }

  if (!presignedUrl) {
    return <Alert severity="warning">PDF URL not available</Alert>
  }

  return (
    <Box>
      {/* PDF Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          color="text.secondary"
          variant="subtitle1"
          sx={{
            fontWeight: 500,
          }}
        >
          {block.fileName}
        </Typography>
        <Button
          variant="text"
          href={presignedUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          startIcon={<Download />}
          sx={{ textTransform: 'none' }}
        >
          Download
        </Button>
      </Box>
      {/* PDF Viewer */}
      {pdfError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pdfError}
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              position: 'relative',
              minHeight: containerHeight ? `${containerHeight}px` : '600px',
              transition: 'none', // Prevent any transition effects
            }}
          >
            {isPageRendering && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            <Document
              file={presignedUrl}
              suspense={false}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                width={width || undefined}
                onRenderSuccess={onPageRenderSuccess}
              />
            </Document>
          </Box>

          {/* Page Navigation */}
          {numPages > 1 && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1 || isPageRendering}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {pageNumber} of {numPages}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  handlePageChange(Math.min(numPages, pageNumber + 1))
                }
                disabled={pageNumber >= numPages || isPageRendering}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}
      {/* Caption */}
      {block.caption && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontStyle: 'italic',
            textAlign: 'center',
            maxWidth: '80%',
            mx: 'auto',
            mt: 2,
            lineHeight: 1.5,
          }}
        >
          {block.caption}
        </Typography>
      )}
    </Box>
  )
}
